import type { PromptResolve } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { RegularModal } from '@/components_deprecated'
import { FAIcon } from '@/components'
import { format } from '@/utils'
import { cn } from '@/lib/utils'

interface SalaryLockLogEntry {
  id: string
  createdAt: string
  wroteDifference: boolean
  lockedBy: { id: string; fullName: string } | null
}

interface SalaryLockLogModalProps {
  onClose: PromptResolve<boolean | null>
  logs: readonly SalaryLockLogEntry[]
  /** Whether a salary base exists for the month (monthReport.isLocked). */
  salaryBaseExists: boolean
}

/**
 * History of who locked the selected user's salary basis for the month, and
 * when. ``lockedBy`` is null for automated/scheduled locks.
 */
export function SalaryLockLogModal({
  onClose,
  logs,
  salaryBaseExists,
}: SalaryLockLogModalProps) {
  const translations = useTranslate({
    title: 'salary.lock-log-title',
    close: 'common.close',
    empty: 'salary.lock-log-empty',
    automatic: 'salary.lock-log-automatic',
    typeDifference: 'salary.lock-log-type-difference',
    typeBase: 'salary.lock-log-type-base',
    unknown: 'common.unknown',
    unknownUser: 'common.unknown-user',
  })

  const sorted = [...logs].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )

  // A base existed before the first log and was never logged.
  // Show it as a synthetic base row with unknown user/time.
  const oldestLog = sorted.at(-1)
  const showImpliedBase =
    salaryBaseExists && (!oldestLog || oldestLog.wroteDifference)

  return (
    <RegularModal
      width="520px"
      cancelText={translations.close}
      onCancel={() => onClose(null)}
      cancelOnClickOutside
      overflowY="auto"
    >
      <div>
        <div className="mb-3 flex items-start justify-between gap-4">
          <h2 className="text-lg font-semibold text-stone-900">
            {translations.title}
          </h2>
          <button
            type="button"
            className="text-stone-400 transition-colors hover:text-stone-600"
            aria-label={translations.close}
            onClick={() => onClose(null)}
          >
            <FAIcon icon="times" />
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto">
          {sorted.length === 0 && !showImpliedBase ? (
            <p className="py-6 text-sm text-stone-500">{translations.empty}</p>
          ) : (
            <ul className="divide-y divide-stone-100">
              {sorted.map(log => (
                <li
                  key={log.id}
                  className="flex items-center justify-between gap-4 py-3"
                >
                  <span className="flex items-center gap-2">
                    <span className="text-sm font-medium text-stone-900">
                      {log.lockedBy?.fullName ?? translations.automatic}
                    </span>
                    <span
                      className={cn(
                        'rounded-full px-2 py-0.5 text-xs font-medium',
                        log.wroteDifference
                          ? 'bg-blue-50 text-blue-700'
                          : 'bg-stone-100 text-stone-600'
                      )}
                    >
                      {log.wroteDifference
                        ? translations.typeDifference
                        : translations.typeBase}
                    </span>
                  </span>
                  <span className="text-sm text-stone-500">
                    {format(new Date(log.createdAt), 'dd.MM.yyyy HH:mm')}
                  </span>
                </li>
              ))}
              {showImpliedBase && (
                <li className="flex items-center justify-between gap-4 py-3">
                  <span className="flex items-center gap-2">
                    <span className="text-sm font-medium text-stone-400">
                      {translations.unknownUser}
                    </span>
                    <span className="rounded-full bg-stone-100 px-2 py-0.5 text-xs font-medium text-stone-600">
                      {translations.typeBase}
                    </span>
                  </span>
                  <span className="text-sm text-stone-400">
                    {translations.unknown}
                  </span>
                </li>
              )}
            </ul>
          )}
        </div>
      </div>
    </RegularModal>
  )
}
